import type { EvaluationField, EvaluationFormType } from './evaluation.model';

export interface EvaluationScaleOption {
  value: number;
  label: string;
}

export interface EvaluationRanking {
  label: string;
  min: number;
}

export interface EvaluationLegendItem {
  score: number;
  title: string;
  text: string;
}

export interface EvaluationFormDefinition {
  value: EvaluationFormType;
  label: string;
  roles: readonly string[];
  scaleMax: number;
  scale: readonly EvaluationScaleOption[] | null;
  rankings: readonly EvaluationRanking[];
  legend: readonly EvaluationLegendItem[];
  fields: EvaluationField[];
}

const FIVE_POINT_SCALE: readonly EvaluationScaleOption[] = [
  { value: 1, label: 'Unsatisfactory' },
  { value: 2, label: 'Satisfactory' },
  { value: 3, label: 'Good' },
  { value: 4, label: 'Very good' },
  { value: 5, label: 'Excellent' }
];

const FIVE_POINT_RANKINGS: readonly EvaluationRanking[] = [
  { label: 'Not accepted', min: 0 },
  { label: 'Acceptable', min: 76 },
  { label: 'Good', min: 81 },
  { label: 'Very good', min: 86 },
  { label: 'Excellent', min: 92 }
];

const FIVE_POINT_LEGEND: readonly EvaluationLegendItem[] = [
  { score: 5, title: 'Excellent', text: 'Consistently exceeds PHL job expectations.' },
  { score: 4, title: 'Very good', text: 'Consistently meets and occasionally exceeds PHL job expectations.' },
  { score: 3, title: 'Good', text: 'Consistently meets PHL job expectations.' },
  { score: 2, title: 'Fair', text: 'Meets minimum requirements for the job.' },
  { score: 1, title: 'Poor', text: 'Overall performance does not meet the minimum acceptable level.' }
];

function joiningDate(): EvaluationField {
  return {
    key: 'joining_date',
    label: 'Joining date',
    type: 'date',
    required: true,
    section: 'Details'
  };
}

function recommendations(): EvaluationField {
  return {
    key: 'recommendations',
    label: 'Elements to be added / comments / recommendations',
    type: 'textarea',
    required: false,
    section: 'Comments'
  };
}

function goals(): EvaluationField[] {
  return [
    {
      key: 'long_term_goal',
      label: 'Long term goal',
      type: 'textarea',
      required: true,
      section: 'Goals'
    },
    {
      key: 'short_term_goal_1',
      label: 'Short term goal 1',
      type: 'textarea',
      required: true,
      section: 'Goals'
    },
    {
      key: 'short_term_goal_2',
      label: 'Short term goal 2',
      type: 'textarea',
      required: true,
      section: 'Goals'
    },
    {
      key: 'short_term_goal_3',
      label: 'Short term goal 3',
      type: 'textarea',
      required: true,
      section: 'Goals'
    }
  ];
}

function checklist(
  key: string,
  label: string,
  low: string,
  mid: string,
  high: string
): EvaluationField {
  return {
    key,
    label,
    type: 'rating',
    required: true,
    section: 'Criteria',
    options: [
      { value: 1, label: low },
      { value: 2, label: mid },
      { value: 3, label: high }
    ]
  };
}

function rated(key: string, label: string, bullets: string[]): EvaluationField {
  return {
    key,
    label,
    type: 'rating',
    required: true,
    section: 'Criteria',
    bullets
  };
}

export const EVALUATION_FORMS: readonly EvaluationFormDefinition[] = [
  {
    value: 'housekeeping',
    label: 'Housekeeping',
    roles: [],
    scaleMax: 3,
    scale: null,
    rankings: [
      { label: 'Not acceptable', min: 0 },
      { label: 'Good', min: 70 },
      { label: 'Very good', min: 80 },
      { label: 'Excellent', min: 90 }
    ],
    legend: [],
    fields: [
      joiningDate(),
      checklist('punctuality', '1. Punctuality', 'Rarely on time', 'Sometimes on time', 'Always on time'),
      checklist(
        'cleanliness',
        '2. General cleanliness',
        'Does not maintain the required cleanliness',
        'Sometimes maintains the required cleanliness',
        'Always maintains the required cleanliness'
      ),
      checklist(
        'attention_to_detail',
        '3. Attention to detail while cleaning',
        'Does not pay attention to details',
        'Sometimes pays attention to some details',
        'Always pays attention to all details'
      ),
      checklist(
        'cleaning_tools',
        '4. Use of cleaning tools',
        'Does not use tools properly',
        'Sometimes uses tools correctly',
        'Always uses tools correctly and effectively'
      ),
      checklist(
        'disinfection',
        '5. Compliance with disinfection standards',
        'Does not comply with disinfection standards',
        'Sometimes complies with disinfection standards',
        'Always complies with disinfection standards'
      ),
      checklist(
        'responsiveness',
        '6. Responsiveness to instruction',
        'Does not respond to instruction',
        'Sometimes responds to instruction',
        'Always responds quickly and follows instruction accurately'
      ),
      checklist(
        'appearance',
        '7. Appearance and personal hygiene',
        'Does not maintain a proper appearance',
        'Sometimes maintains a good appearance',
        'Always maintains a neat and clean appearance'
      ),
      checklist(
        'reporting',
        '8. Reporting issues or shortages',
        'Does not report an issue unless asked',
        'Sometimes reports issues',
        'Always reports issues promptly and clearly'
      ),
      checklist(
        'interaction',
        '9. Interaction with staff and patients',
        'Does not interact appropriately',
        'Sometimes interacts well',
        'Always interacts respectfully and kindly'
      ),
      checklist(
        'overall',
        '10. Overall performance',
        'Performance needs improvement',
        'Sometimes performs well',
        'Always performs excellently'
      ),
      recommendations()
    ]
  },
  {
    value: 'physiotherapy',
    label: 'Physiotherapy',
    roles: ['doctor'],
    scaleMax: 5,
    scale: FIVE_POINT_SCALE,
    rankings: FIVE_POINT_RANKINGS,
    legend: FIVE_POINT_LEGEND,
    fields: [
      joiningDate(),
      ...goals(),
      rated('clients_rights', "1. Client's rights", [
        'Respect and dignity',
        'Privacy',
        'Client communication',
        'Informed consent'
      ]),
      rated('care_plan', '2. Care plan development', [
        'Elaborates a care plan',
        'Involves the family in the care plan',
        'Applies the elaborated care plan'
      ]),
      rated('safety', '3. Safety', [
        'Recognizes own limits',
        'Requests assistance when necessary'
      ]),
      rated('infection_control', '4. Infection control', [
        'Hand washing or hand rub before the session',
        'Hand washing or hand rub after the session',
        'Wears gloves when applicable',
        'Wears a face mask when applicable',
        'Manages contaminated equipment properly',
        'Disinfects and sterilizes equipment used for the patient'
      ]),
      rated('occupational_health', '5. Occupational health', [
        'Follows proper ergonomics at the workplace',
        'Handles the patient safely and correctly'
      ]),
      rated('professional_behavior', '6. Professional behavior', [
        'Punctual and dependable',
        'Committed to the session duration',
        'Dressed appropriately',
        'Shows initiative and responsibility for learning',
        'Represents the profession effectively'
      ]),
      rated('ethical_legal', '7. Ethical and legal principles', [
        'Follows PHL policies, rules, and regulations',
        'Maintains confidentiality',
        'Obtains informed consent',
        'Aware of professional roles and scope of practice'
      ]),
      rated('communication', '8. Communication', [
        'Develops rapport',
        'Listens actively and attentively',
        'Adapts questions, technique, and terminology to the situation',
        'Sensitive to the client, family, and staff comfort and non-verbal cues',
        'Reads the medical record, chart, or images and uses that information',
        'Interviews the patient or family in a systematic and professional way',
        'Gives instructions, advice, and a home program'
      ]),
      rated('reporting', '9. Reporting and documenting', [
        'Quality of written and verbal reports',
        'Reports are completed on time',
        'Meets documentation requirements'
      ]),
      rated('theory', '10. Application of theory', [
        'Presents the client case history',
        'Discusses physical therapy scope of practice and roles',
        'Applies clinical concepts such as informed consent, determinants of health, interview, teamwork, client-centered care, cultural competence, and learning styles'
      ]),
      rated('clinical_reasoning', '11. Clinical reasoning and critical thinking', [
        'Referral decision',
        'Technical aids provided',
        'Appropriate treatment environment and tools',
        'Formulates relevant questions',
        'Positive attitude toward learning and suggestions',
        'Seeks further information and resources, including scientific literature',
        'Offers ideas and rationale for clinical situations'
      ]),
      rated('supervision', '12. Supervision of others', [
        'PT assistants, technicians, junior colleagues, and students'
      ]),
      rated('goals_targets', '13. Achieving goals and targets', [
        'Sets SMART goals and achieves them within the time frame'
      ]),
      rated('professional_matter', '14. Professional matter', [
        'Adapts to change',
        'Develops new approaches',
        'Encourages parents to take responsibility',
        'Willing to accept additional responsibilities'
      ]),
      rated('language', '15. Language skills', [
        'Spoken English',
        'Reading and writing English'
      ]),
      rated('voluntary', '16. Voluntary work', [
        'Level of acceptance and cooperation'
      ]),
      recommendations()
    ]
  },
  {
    value: 'receptionist',
    label: 'Receptionist',
    roles: [],
    scaleMax: 5,
    scale: FIVE_POINT_SCALE,
    rankings: FIVE_POINT_RANKINGS,
    legend: FIVE_POINT_LEGEND,
    fields: [
      joiningDate(),
      ...goals(),
      rated('clients_rights', "1. Client's rights", [
        'Respect and dignity',
        'Privacy',
        'Client communication',
        'Informed consent'
      ]),
      rated('safety', '2. Safety', [
        'Recognizes own limits',
        'Requests assistance when necessary'
      ]),
      rated('infection_control', '3. Infection control', [
        'Hand washing or hand rub when applicable',
        'Wears gloves when applicable',
        'Wears a face mask when applicable',
        'Manages contaminated equipment properly',
        'Disinfects and sterilizes equipment used for the patient'
      ]),
      rated('occupational_health', '4. Occupational health', [
        'Follows proper ergonomics at the workplace'
      ]),
      rated('professional_behavior', '5. Professional behavior', [
        'Punctual and dependable',
        'Committed to the work',
        'Dressed appropriately',
        'Shows initiative and responsibility for learning'
      ]),
      rated('ethical_legal', '6. Ethical and legal principles', [
        'Follows PHL policies, rules, and regulations',
        'Maintains confidentiality',
        'Obtains informed consent'
      ]),
      rated('communication', '7. Communication', [
        'Develops rapport',
        'Listens actively and attentively',
        'Adapts questions, technique, and terminology to the situation',
        'Sensitive to the client, family, and staff comfort and non-verbal cues'
      ]),
      rated('goals_targets', '8. Achieving goals and targets', [
        'Sets SMART goals and achieves them within the time frame'
      ]),
      rated('supervision', '9. Supervision of others', [
        'Technicians and more junior colleagues'
      ]),
      rated('professional_matter', '10. Professional matter', [
        'Adapts to change',
        'Develops new approaches',
        'Encourages parents to take responsibility',
        'Willing to accept additional responsibilities'
      ]),
      rated('language', '11. Language skills', [
        'Spoken English',
        'Reading and writing English'
      ]),
      rated('voluntary', '12. Voluntary work', [
        'Level of acceptance and cooperation'
      ]),
      recommendations()
    ]
  }
];

export function formDefinition(formType: string | null | undefined): EvaluationFormDefinition | null {
  return EVALUATION_FORMS.find(form => form.value === formType) ?? null;
}
